import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId, EASE } from "@/lib/site";

const LINES = [["Dalla", "demolizione"], ["alle", "chiavi."]];

const Line = ({ words, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.3, delay: 0.25 + i * 0.16, ease: EASE }}>
      {words[0]} <em className="italic text-[#e8c063] font-light">{words[1]}</em>
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 60, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden grain bg-[#0d0d0e]">
      <div className="absolute inset-0 grid lg:grid-cols-12">
        <div className="hidden lg:block lg:col-span-6" />
        <div className="relative lg:col-span-6 h-full [perspective:1400px]">
          <motion.div style={{ y: imgY, rotateX: rx, rotateY: ry }}
            initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.8, delay: 0.1, ease: EASE }}
            className="absolute inset-0 lg:inset-y-[12%] lg:right-12 lg:left-0 will-change-transform">
            <motion.img src="/img/h1.jpg" alt="Ingresso ristrutturato con parquet a spina e boiserie"
              initial={{ scale: 1.25 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: EASE }}
              className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0e] via-[#0d0d0e]/55 to-[#0d0d0e]/30 lg:from-[#0d0d0e]/40 lg:via-transparent lg:to-transparent" />
          </motion.div>
        </div>
      </div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 min-h-[100svh] flex flex-col justify-end lg:justify-center pb-20 lg:pb-0 pt-32">
        <motion.p className="label mb-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 1 }}>
          Ristrutturazioni chiavi in mano · Roma e dintorni
        </motion.p>
        <h1 data-testid="hero-title" className="font-display font-light text-[#f4f1ea] text-5xl sm:text-7xl lg:text-[7.2rem] leading-[0.95] tracking-tight max-w-4xl">
          {LINES.map((w, i) => <Line key={i} words={w} i={i} />)}
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 1.1, ease: EASE }}
          className="mt-10 max-w-md lg:max-w-lg">
          <p data-testid="hero-description" className="text-base md:text-lg text-[#d8d2c6] leading-relaxed">
            <strong className="font-medium text-[#f4f1ea]">Slusariuc Costruzioni</strong> realizza ristrutturazioni complete per case e
            appartamenti di pregio a Roma e dintorni. Un solo referente segue ogni fase del cantiere e
            fornisce direttamente serramenti, pavimenti, rivestimenti, rubinetteria e arredamento.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <button data-testid="hero-cta-primary" onClick={() => scrollToId("contatti")}
              className="group inline-flex items-center gap-3 bg-[#d4a73c] text-[#0d0d0e] px-8 py-4 text-xs tracking-[0.25em] uppercase hover:bg-[#e8c063] transition-colors duration-500">
              Richiedi un sopralluogo
              <ArrowDownRight size={16} className="transition-transform duration-500 group-hover:rotate-[-45deg]" />
            </button>
            <button data-testid="hero-cta-secondary" onClick={() => scrollToId("metodo")}
              className="text-xs tracking-[0.25em] uppercase text-[#e9e4da] hover:text-[#e8c063] border-b border-[#d4a73c]/50 pb-1 transition-colors">
              Il nostro metodo
            </button>
          </div>
        </motion.div>
      </motion.div>

    </section>
  );
};
